import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {ConfigPageComponent} from 'features/core/shared/mvs-page/config-page.component';
import {WidgetConfigInterface} from 'features/core/shared/mvs-page/interface/widget-config-page.interface';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {PageHeaderConfig} from 'features/core/shared/service/page/page-header-config.interface';
import {
    MvsConfigHeaderComponent
} from 'features/core/shared/components/mvs-config-header/mvs-config-header.component';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {
    buildCpProvisionAgentGroupQuery,
    buildCpWidgetForm,
    CP_PROVISION_AGENT_GROUP_COLUMNS
} from '../../service/domain/cp-widget-column';

/**
 * Alias and label of the group-membership table.
 *
 * They are constants rather than inline literals for ONE reason: this is the only widget on the
 * page that also names itself inside a `createFunction`, so the two strings would otherwise exist
 * twice. `ui.UiWidget.alias` is a globally unique, DB-backed identity that the user's column widths
 * and sorting hang off — a rename that reached only one of the two copies would hand the widget an
 * identity different from the one the map declares, and lose that state without any error.
 */
const CP_AGENT_GROUP_WIDGET_ALIAS = 'config.cp.ProvisionAgentGroup.table';
const CP_AGENT_GROUP_WIDGET_LABEL = 'Gruppenzuordnungen (Perioden)';

/**
 * Configuration page of the cp module (outgoing commission) — 04-frontend.md §6.
 *
 * CONFIG-001: exactly ONE config route per module — `cp/config` (hash routing: `/#/cp/config`).
 * Navigation runs through `?navItem={action}`; every action value is the ObjectType alias of the
 * entity it maintains, e.g. `/#/cp/config?navItem=cp.ProvisionLiabilityType`.
 *
 * ## Reference implementation
 * This page follows `features/feature-bm/bd/page/bd-config/bd-config.page.ts` — the module that
 * solves EXACTLY this situation: `bd.DunningType` owns many same-module configuration children
 * and is therefore rendered as ONE flat table whose row click leaves the config page. The children
 * are NOT part of the config page at all; they live in
 * `features/feature-bm/bd/component/object/dunning-type-object/dunning-type-object.component.ts`,
 * registered in `features/feature-bm/bd/bd.entity-provider.ts:33`
 * (`['bd.DunningType', {..., objectComponent: [{component: DunningTypeObjectComponent}]}]`) and
 * declared in `features/feature-bm/bd/bd.module.ts:240`.
 *
 * The declarative `widgets` map, the selectable/`showSearch` post function and the
 * `MvsConfigHeaderComponent` page header follow the second reference,
 * `features/feature-bm/bm/page/overview/config/config.page.ts` (PAT §M.2).
 *
 * ## The P-16 build (04-frontend.md §6)
 * `cp.ProvisionType` owns ELEVEN same-module configuration children and is therefore a COMPLEX
 * configuration entity: the page shows it as a flat table, the row click opens the object page
 * (CONFIG-003 / CONFIG-015), and the children live behind that object page's LEFT NAVIGATION. 04
 * §6 states this build ("Verbindlich ist die bd-Bauform") and names the alternative an
 * architectural error ("Eine Inline-Bauform verletzt die Complex Configuration Entity Rule
 * (CONFIG-003) und ist mit 11 Kindern endgültig untragbar").
 *
 * For the same reason NO entity with a mandatory `provisionType` FK is a top-level navItem here
 * (CONFIG-010) — `cp.ProvisionTypeMatrix` and `cp.ProvisionTypeContractEvent` included, even
 * though both are themselves parents of one further child.
 *
 * ## Where the eleven children live
 * `component/object-components/cp-provision-type-object/` — root `ObjectBaseComponent` plus
 * `view/base` (the LEFT NAVIGATION), `view/full`, `view/side` and the shared detail components
 * (PAT §L, same shape as the in-module `cp-provision-agent-object`). Registered in
 * `cp.entity-provider.ts` (`['cp.ProvisionType', {..., objectComponent: [{mode:
 * MvsCrudModeEnum.update, component: CpProvisionTypeObjectComponent}]}]`) and declared in
 * `cp.module.ts`, so the row click below resolves onto it (CONFIG-015 step 3).
 *
 * The child widget aliases are the ones 04 §6 assigns:
 *   Gruppen              `config.cp.ProvisionTypeGroup.table`
 *   Provisionsbasen      `config.cp.ProvisionTypeProvisionBase.table`
 *   Provisionsarten      `config.cp.ProvisionTypeComponent.table`
 *   Provisionsereignisse `config.cp.ProvisionTypeContractEvent.selectable`
 *                        + `config.cp.ProvisionTypeContractEventPriceReason.table` (level 4a)
 *   Bedingungssätze      `config.cp.ProvisionAssignmentConditionSet.table`
 *                        + `config.cp.ProvisionAssignmentCondition.table` (level 2 of the set)
 *   Relevanz             `config.cp.ProvisionTypeContractEligibility.table`
 *   Basiswert-Zuordnung  `config.cp.ProvisionTypeContractProvisionBase.table`
 *   Haftungs-Zuordnung   `config.cp.ProvisionTypeContractLiability.table`
 *   Matrix-Zuordnung     `config.cp.ProvisionTypeMatrixAssignment.table`
 *   Matrizen             `config.cp.ProvisionTypeMatrix.selectable`
 *                        + `config.cp.ProvisionTypeMatrixAgent.table` (level 9a)
 *   Buchungsereignisse   `config.cp.ProvisionTypeTransactionEvent.table`
 *   Konfigurationsprüfung  the CR-001…CR-032 report panel, no entity list
 *
 * ## The check report moved with them
 * `GET /mvsa/cp/configCheck/provisionType/{id}` is scoped to ONE provision type. 04 §6 lists it as
 * child 11 of the object page and does NOT list it among the config page's top-level navItems, so
 * it lives in the object page now, where the provision type is already the context and the picker
 * this page used to need is gone.
 *
 * ## Terminology (04 §1, AX-10)
 * „Provisionstyp" = `cp.ProvisionType` (this page's table); „Provisionsart" =
 * `cp.ProvisionTypeComponent`; „Provisionsereignis" = `cp.ProvisionTypeContractEvent`.
 * „Buchungskomponente" is technical and never appears in the UI.
 *
 * ## Condition sets (assignment-condition redesign)
 * `cp.ProvisionAssignmentConditionSet` ("Bedingungssatz") has a mandatory `provisionType` FK, so —
 * like every other provision-type child — it is NOT a top-level navItem here (CONFIG-010) but the
 * object page's navItem `conditionSets`. It owns exactly one configuration child,
 * `cp.ProvisionAssignmentCondition` ("Bedingung"), and is therefore SIMPLE there: the selectable
 * set table plus the set-filtered condition table on one nav point. The six assignment families
 * (K-05, K-06, K-07, K-10, K-17, K-18) only REFERENCE a set through an optional FK — they are owned
 * by `cp.ProvisionType` and maintained on their own navItems, so they do not count as set children
 * (same reading as K-10 referencing a matrix, CONFIG-003 child counting rules). Both new entities
 * are thus covered by this page through the object page (CONFIG-004); the two aliases are listed
 * above and are declared in `cp-provision-type-condition-set.component.ts`.
 *
 * ### ACCEPTED EXCEPTION — CONFIG-003 for `cp.ProvisionAssignmentConditionSet` (user decision A)
 * The entity registry lists SEVEN same-module reverse FKs on the set (the condition plus the six
 * assignment families), so a mechanical CONFIG-003 count makes it "complex" and demands an object
 * page of its own. That is accepted as an exception, not followed: the set is a CHILD of
 * `cp.ProvisionType` (mandatory FK) and is edited ONLY in the provision type object's navItem
 * „Bedingungssätze“ (a Review Workbench: set table left, the selected set with its conditions and
 * its „Verwendet von“ list right). Six of the seven FKs are optional REFERENCES from assignments the
 * provision type owns; the set owns exactly one child. An object page for the set would put a third
 * navigation level under the provision type for one child table. Mechanism and rules:
 * fe_core_config-page-audit → "Accepted Exceptions".
 * @config-audit-accepted CONFIG-003 cp.ProvisionAssignmentConditionSet: child of cp.ProvisionType, edited only in its object page navItem „Bedingungssätze“; owns one child (condition), the six assignment FKs are optional references (user decision A)
 * @config-audit-accepted CONFIG-015 cp.ProvisionAssignmentConditionSet: no own click chain — reached through the cp.ProvisionType object page (see CONFIG-003 exception above)
 *
 * ## Nesting inside the object page
 * `cp.ProvisionTypeMatrix` and `cp.ProvisionTypeContractEvent` stay SIMPLE there: each owns
 * exactly ONE configuration child (`ProvisionTypeMatrixAgent`, `ProvisionTypeContractEventPriceReason`),
 * so the selectable parent pattern applies to both and each gets its own two-level nav point.
 * `cp.ProvisionTypeMatrixAssignment` is owned by `cp.ProvisionType` (mandatory FK
 * `provisionTypeDtoId`) and merely REFERENCES a matrix as its assignment result, so it does not
 * count as a matrix child (CONFIG-003 child counting rules).
 *
 * ## Lookup catalogues stay flat
 * `cp.ProvisionAgentTransactionType` is referenced by `cp.ProvisionType#payoutTransactionType` and
 * `cp.ProvisionContractTransactionType#agentTransactionType`, and
 * `cp.ProvisionContractTransactionType` by `cp.ProvisionTypeTransactionEvent`. Those are lookup
 * REFERENCES, not ownership: neither referencing entity is owned by the transaction type (no
 * mandatory parent FK pointing at it, no cascade). Both therefore have zero owned children and
 * stay flat tables — the same reading bd applies to `bd.DunningTypeManualTransaction` pointing at
 * `bm.CustomerBillingTransactionType`.
 *
 * `check-config-page.js` cannot make that distinction — it counts EVERY same-module
 * `ConfigurableEntity` with an FK as a child — and therefore reports CONFIG-003 for
 * `cp.ProvisionAgentTransactionType` with the "children" `cp.ProvisionContractTransactionType` and
 * `cp.ProvisionType`. Following the finding would register an object page whose left navigation
 * lists the MODULE ROOT as a child of a booking-type lookup, i.e. it would invert the ownership
 * direction of the whole configuration tree. The flat table stands; 04-frontend.md §6 lists this
 * entity as a top-level navItem of exactly this page.
 *
 * ## No selectable flow on this page — and why CONFIG-011 reports one finding
 * Every nav item here renders ONE flat, full-width table; all parent-child flows moved into the
 * two object pages. `check-config-page.js` decides CONFIG-011 by SEARCHING THIS FILE'S TEXT for
 * the two marker literals (the selectable ui-component assignment and the filter-criteria factory
 * call); its own SKILL.md marks the rule "Minimal [MANUAL REVIEW REQUIRED]". A page that
 * legitimately has neither therefore gets one finding per parent-child pair among its entities.
 * The current run of `check-config-page.js --module cp` reports exactly ONE:
 *   `cp.ProvisionAgent` -> `cp.ProvisionAgentGroup`
 *
 * The group assignments stay a cross-agent overview list here on purpose: 04 §6 assigns BOTH
 * `config.cp.ProvisionAgent.table` and `config.cp.ProvisionAgentGroup.table` to this nav item, and
 * the single-agent period chain is maintained in the ProvisionAgent object page (04 §7).
 *
 * Two consequences for whoever edits this file next. First, do NOT silence the findings by adding
 * an unused selectable — that satisfies the marker, not the rule. Second, and less obviously:
 * because the check reads the whole file, PROSE mentioning either marker literal verbatim flips
 * the result just as code would. That is why this paragraph names them descriptively.
 *
 * ## Lazy rendering (CONFIG-022)
 * `ConfigPageComponent.initializeWidgets()` only builds the `WidgetData` objects; it fires no
 * query (`widget.factory.ts` merely fills `dataProviderListRequest`). The QL request runs when
 * `<mvs-widget>` is actually instantiated, so every widget in the template sits behind an `@if`
 * on the active navItem / inner tab. On page load exactly the default navItem's query fires.
 *
 * ## Widget aliases
 * New style `config.cp.{EntityName}.{variant}` (CLAUDE.md, PAT §M.2). The alias is the stable
 * widget identity for column widths / sorting / filters and must stay unchanged across redeploys.
 *
 * ## Coverage of the 28 cp aliases
 * Maintainable from this page: `cp.ProvisionType` (plus its children via the object page, condition sets and conditions included),
 * `cp.ProvisionLiabilityType`, `cp.ProvisionAgentTransactionType`,
 * `cp.ProvisionContractTransactionType`, `cp.ProvisionAgent` and `cp.ProvisionAgentGroup`. The
 * remaining runtime entities (R-03…R-12) are deliberately not config content — their surfaces are
 * the provision dashboard (04 §5) and the ProvisionAgent object component (04 §7).
 */
@Component({
    selector: 'cp-config-page',
    templateUrl: './cp-config.page.html',
    styleUrls: ['./cp-config.page.scss'],
    standalone: false
})
export class CpConfigPage extends ConfigPageComponent implements OnInit {

    // =========================================================================
    // navItem actions (04 §6) — `?navItem={action}`
    // =========================================================================
    static readonly NAV_PROVISION_TYPE = 'cp.ProvisionType';
    static readonly NAV_PROVISION_LIABILITY_TYPE = 'cp.ProvisionLiabilityType';
    static readonly NAV_PROVISION_AGENT_TRANSACTION_TYPE = 'cp.ProvisionAgentTransactionType';
    static readonly NAV_PROVISION_CONTRACT_TRANSACTION_TYPE = 'cp.ProvisionContractTransactionType';
    static readonly NAV_PROVISION_AGENT = 'cp.ProvisionAgent';

    defaultLabel: string = 'Provisionskonfiguration';

    /** Active navItem action — drives the section switch in the template. */
    activeNavItem: string;

    /**
     * Navigation entries (04 §6). `action` = ObjectType alias and at the same time the `?navItem=`
     * value. Exactly ONE default (CONFIG-006), `fa-solid` icons only (CONFIG-017), German labels
     * with real umlauts (CONFIG-018).
     *
     * `cp.ProvisionTypeMatrix` is deliberately absent: it is a child of `cp.ProvisionType`
     * (mandatory FK) and lives in that entity's object page (CONFIG-010).
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Provisionstypen',
            action: CpConfigPage.NAV_PROVISION_TYPE,
            tooltip: 'Provisionstypen — Zeile öffnen für Provisionsarten, Ereignisse, Basen, '
                + 'Matrizen, Zuordnungen und den Prüfbericht',
            icon: 'fa-solid fa-folder-tree',
            toggleable: false,
            default: true
        },
        {
            label: 'Haftungsarten',
            action: CpConfigPage.NAV_PROVISION_LIABILITY_TYPE,
            tooltip: 'Haftungsarten der Provisionsrückforderung',
            icon: 'fa-solid fa-shield-halved',
            toggleable: false,
            default: false
        },
        {
            label: 'Kontobuchungsarten',
            action: CpConfigPage.NAV_PROVISION_AGENT_TRANSACTION_TYPE,
            tooltip: 'Buchungsarten der Provisions- und Rücklagenkonten',
            icon: 'fa-solid fa-money-check-dollar',
            toggleable: false,
            default: false
        },
        {
            label: 'Vertragsbuchungsarten',
            action: CpConfigPage.NAV_PROVISION_CONTRACT_TRANSACTION_TYPE,
            tooltip: 'Buchungsarten der Vertragsprovisionen',
            icon: 'fa-solid fa-file-invoice-dollar',
            toggleable: false,
            default: false
        },
        {
            label: 'Abrechnungskonten',
            action: CpConfigPage.NAV_PROVISION_AGENT,
            tooltip: 'Abrechnungskonten der Berater mit Rücklagenkonto und Gruppenzugehörigkeit',
            icon: 'fa-solid fa-user-tie',
            toggleable: false,
            default: false
        }
    ];

    /**
     * Declarative widget map (CONFIG-005) — every widget of this page is static and unfiltered,
     * so all but one are pure `widgetData: null` placeholders (same shape as the bd reference
     * page). That removes the four undocumented factory calls the widget audit flagged
     * (WDG2 / WIDGET-004) instead of papering over them with AI Javadoc blocks: the dynamic,
     * parent-filtered child widgets they built belong to the ProvisionType object page now.
     *
     * `ConfigPageComponent.initializeWidgets()` builds one `WidgetData` per entry (table,
     * `resolve_fkey_texts = true`). No entry needs a `postFunction`: every widget on this page is
     * a flat table — the selectable parent-child flows all live in the ProvisionType object page.
     *
     * THE ONE EXCEPTION is `provisionAgentGroupWidget`, which supplies a `createFunction` and is
     * therefore the only imperative `WidgetFactory` call left on this page. It is not a relapse
     * into the pattern the audit removed: that finding was about dynamic, parent-filtered widgets
     * built in page code. This one is static and unfiltered like all the others and differs only
     * in HOW its columns are resolved — `cp.ProvisionAgentGroup` is a nameless link entity whose
     * agent column sits two hops away, which `resolve_fkey_texts` cannot reach, so the default
     * builder renders a foreign-key id where a Beratername belongs. See the entry itself.
     *
     * No query fires here — it runs when the matching `<mvs-widget>` is rendered, and each one
     * sits behind an `@if` on the active navItem / inner tab (CONFIG-022).
     */
    override widgets: { [key: string]: WidgetConfigInterface } = {

        // --- navItem cp.ProvisionType (default) -------------------------------------------
        // COMPLEX configuration entity (ten same-module children): flat table only, the row
        // click leaves the page and opens the object page (CONFIG-003 / CONFIG-015), exactly like
        // `bd.config.dunning.type` in bd-config.page.ts.
        provisionTypeWidget: {
            alias: 'config.cp.ProvisionType.table',
            label: 'Provisionstypen',
            entity: 'cp.ProvisionType',
            widgetData: null
        },

        // --- navItem cp.ProvisionLiabilityType (global catalogue, 04 §6) -------------------
        provisionLiabilityTypeWidget: {
            alias: 'config.cp.ProvisionLiabilityType.table',
            label: 'Haftungsarten',
            entity: 'cp.ProvisionLiabilityType',
            widgetData: null
        },

        // --- navItem cp.ProvisionAgentTransactionType --------------------------------------
        provisionAgentTransactionTypeWidget: {
            alias: 'config.cp.ProvisionAgentTransactionType.table',
            label: 'Kontobuchungsarten',
            entity: 'cp.ProvisionAgentTransactionType',
            widgetData: null
        },

        // --- navItem cp.ProvisionContractTransactionType -----------------------------------
        provisionContractTransactionTypeWidget: {
            alias: 'config.cp.ProvisionContractTransactionType.table',
            label: 'Vertragsbuchungsarten',
            entity: 'cp.ProvisionContractTransactionType',
            widgetData: null
        },

        // --- navItem cp.ProvisionAgent: inner tab 1 ----------------------------------------
        // Row click opens the ProvisionAgent object component (CpProvisionAgentObjectComponent,
        // registered in cp.entity-provider.ts) — CONFIG-015.
        provisionAgentWidget: {
            alias: 'config.cp.ProvisionAgent.table',
            label: 'Abrechnungskonten',
            entity: 'cp.ProvisionAgent',
            widgetData: null
        },

        // --- navItem cp.ProvisionAgent: inner tab 2 ----------------------------------------
        // Period chain per agent (R-02). A SINGLE chain is maintained in the "Gruppen" nav item of
        // the ProvisionAgent object component (04 §7); this is the cross-agent overview.
        //
        // THE ONE ENTRY OF THIS PAGE THAT BUILDS ITS OWN WIDGET, and it has to. Every other entry
        // is a flat table over an entity that carries its own name, which the default builder
        // resolves. `cp.ProvisionAgentGroup` is a pure LINK entity: it has no name at all, and its
        // "Vermittler" column points at `cp.ProvisionAgent`, which has none either and derives its
        // display name from a getter over `am.Agent` that falls back to the id. `resolve_fkey_texts`
        // never reaches two hops, so under the default builder that column renders a NUMBER.
        // Explicit joins are the remedy; the query and its curated columns live in
        // `service/domain/cp-widget-column.ts`, together with the reason every leg is a LEFT join.
        //
        // `createFunction` is the framework's own seam for this: `initializeWidgets()` calls it and
        // takes its RETURN VALUE as the widget (`config-page.component.ts:38-41`). Assigning the
        // widget inside the callback instead would not work — it is invoked with NO arguments.
        provisionAgentGroupWidget: {
            alias: CP_AGENT_GROUP_WIDGET_ALIAS,
            label: CP_AGENT_GROUP_WIDGET_LABEL,
            entity: 'cp.ProvisionAgentGroup',
            widgetData: null,

            /**
             * AI:
             * Status: "in progress"
             * Type: Widget
             * SubType: WidgetConfig
             * Reason: Cross-agent overview of the periodised group memberships
             *     (cp.ProvisionAgentGroup). QL view with explicit joins onto the agent
             *     (two hops, to am.Agent), the provision type and the — optional — agent group,
             *     so the table shows plain names instead of foreign-key ids. The alias is
             *     unchanged, so the user's column widths and sorting survive.
             */
            createFunction: () => WidgetFactory.createWidgetTableQl(
                CP_AGENT_GROUP_WIDGET_ALIAS,
                CP_AGENT_GROUP_WIDGET_LABEL,
                buildCpProvisionAgentGroupQuery('config.cp.ProvisionAgentGroup'),
                WidgetDataParam.create('widgetForm',
                    buildCpWidgetForm(CP_PROVISION_AGENT_GROUP_COLUMNS)))
        }

    };

    // =========================================================================
    // navItem cp.ProvisionAgent — two inner tabs (CONFIG-013)
    // =========================================================================

    agentTabIndex: number = 0;

    /** Previous navItem action — distinguishes a real nav change from a param-triggered re-fire. */
    private previousNavItemAction: string | null = null;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService,
        protected router: Router
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Provisionskonfiguration';
        // ConfigPageComponent.ngOnInit() first runs PageComponent.ngOnInit(), where
        // subscribeToParams() subscribes to the query params and therefore triggers
        // postNavigationSelection() synchronously once — and only afterwards initializeWidgets().
        super.ngOnInit();
        this.initialized = true;
    }

    /**
     * Page header in component mode (CONFIG-008) — no inline header in the template.
     */
    override getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Provisionskonfiguration',
                categoryTypeMode: 'configuration',
                icon: 'fa-solid fa-percent',
                links: [
                    {label: 'Provisions-Dashboard', icon: 'fa-solid fa-gauge', url: 'cp/dashboard'}
                ]
            }
        };
    }

    /**
     * Required override for ConfigPageComponent (CONFIG-006): hands the navigation entries to the
     * shell so it renders the navItem sidebar. `PageComponent.isNavComponent()` returns null, so
     * without this override the page would show only the default navItem's content and the other
     * five entries would be reachable by `?navItem=` URL only. Same shape as the bd reference page.
     */
    override isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    /**
     * Lifecycle of the active navItem (CONFIG-020):
     *   1. restore state from the query params (reload / deep link / back),
     *   2. clear the navItem-local params when leaving — otherwise the user would return to a
     *      filtered child view while the parent list shows no selection.
     *
     * There is no lazy widget creation left to do: every widget is declarative and the template
     * `@if` gates keep the queries on demand (CONFIG-022).
     */
    override postNavigationSelection(): void {

        const queryParams: ParamMap = this.route.snapshot.queryParamMap;
        const action: string | null = this.activeNavigationItem?.action ?? null;
        const navItemChanged: boolean = action !== this.previousNavItemAction;

        this.activeNavItem = action;

        // Inner tab index: restore from the URL, but only on a genuine nav-item change — a re-fire
        // caused by our own selection write must not reset the open tab.
        if (navItemChanged) {
            this.agentTabIndex = this.readTabIndex(queryParams, 'agentTabIndex', 0);
        }

        const clearedParams: { [key: string]: null } = {};

        // --- navItem cp.ProvisionAgent -------------------------------------------------------
        if (action !== CpConfigPage.NAV_PROVISION_AGENT && navItemChanged
            && queryParams.get('agentTabIndex')) {
            clearedParams['agentTabIndex'] = null;
        }

        // A `checkProvisionTypeId` left over in a bookmarked URL belongs to the retired
        // `cp.configCheck` nav item (the check report moved into the ProvisionType object page).
        // Strip it so an old link opens a clean page instead of carrying a dead parameter.
        if (navItemChanged && queryParams.get('checkProvisionTypeId')) {
            clearedParams['checkProvisionTypeId'] = null;
        }

        if (Object.keys(clearedParams).length) {
            this.router.navigate([], {
                queryParams: clearedParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }

        this.previousNavItemAction = action;
    }

    // =========================================================================
    // navItem cp.ProvisionType
    // =========================================================================

    /**
     * CONFIG-015 step 2 — the row click on the COMPLEX entity `cp.ProvisionType` leaves the config
     * page and opens the object page, where `CpProvisionTypeObjectComponent` renders the ten children
     * plus the configuration check behind its left navigation.
     */
    handleProvisionTypeSelect(event: ObjectIdentifier | ObjectIdentifierData): void {
        this.handleObjectSelect(event, CpConfigPage.NAV_PROVISION_TYPE);
    }

    // =========================================================================
    // navItem cp.ProvisionAgent
    // =========================================================================

    onAgentTabChange(index: string | number): void {
        const tabIndex = Number(index);
        this.agentTabIndex = tabIndex;
        this.router.navigate([], {
            queryParams: {agentTabIndex: tabIndex || null},
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    // =========================================================================
    // Shared helpers
    // =========================================================================

    /**
     * Standard row click (CONFIG-019): opens the record through the object navigation in the
     * sidebar. `objectType` is passed in by the caller because QL-built widgets leave their
     * `dataProviderObject` empty (`widget.factory.ts` sets it to ""), so the event carries no
     * reliable type.
     */
    handleObjectSelect(event: ObjectIdentifier | ObjectIdentifierData, objectType: string): void {
        const objectId = this.resolveObjectId(event);
        if (!objectId || !objectType) {
            return;
        }
        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            objectType,
            objectId,
            'object', '', '', '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(navigationEntry, 'main');
    }

    /** Robust id extraction — selectable and table widgets both fill `objectId`, QL rows `e.id`. */
    private resolveObjectId(event: ObjectIdentifier | ObjectIdentifierData): number | null {
        if (!event) {
            return null;
        }
        if (event.objectId) {
            return +event.objectId;
        }
        const data = (event as ObjectIdentifierData).data;
        return data && data['e.id'] ? +data['e.id'] : null;
    }

    private readTabIndex(queryParams: ParamMap, param: string, fallback: number): number {
        const raw = queryParams.get(param);
        if (raw === null || raw === undefined || raw === '') {
            return fallback;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    }

}
